import styles from './caixa.module.css';
import React, { useEffect, useCallback, useMemo } from "react";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDebounce } from 'use-debounce';
import api from "../../services/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LuSearch } from "react-icons/lu";
import Spinner from '../../components/Spinner/carregando';
import ui from "../../styles/ui.module.css";
import { convertCurrency, fetchRatesForDate } from "../../utils/currencyRates";

const normalizeDecimalInput = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim().replace(/\s+/g, "").replace(",", ".");
};

const parseDecimalInput = (value) => {
  const normalized = normalizeDecimalInput(value);
  if (!normalized) return NaN;
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
};

// Esquema de validação com Yup
const schema = yup.object({
  filtroData: yup
    .string()
    .required('Data é obrigatória')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Formato de data inválido'),
  USD: yup
    .number()
    .transform((_, originalValue) => parseDecimalInput(originalValue))
    .typeError('Taxa USD deve ser um número')
    .positive('Taxa USD deve ser positiva')
    .required('Taxa USD é obrigatória')
    .test('maxDecimals', 'Taxa USD deve ter até 2 casas decimais', (_value, context) => {
      const original = context?.originalValue;
      const normalized = normalizeDecimalInput(original);
      if (!normalized) return true;
      return /^\d+([.,]\d{1,2})?$/.test(normalized);
    }),
  BRL: yup
    .number()
    .transform((_, originalValue) => parseDecimalInput(originalValue))
    .typeError('Taxa BRL deve ser um número')
    .positive('Taxa BRL deve ser positiva')
    .required('Taxa BRL é obrigatória')
    .test('maxDecimals', 'Taxa BRL deve ter até 2 casas decimais', (_value, context) => {
      const original = context?.originalValue;
      const normalized = normalizeDecimalInput(original);
      if (!normalized) return true;
      return /^\d+([.,]\d{1,2})?$/.test(normalized);
    }),
  BOB: yup
    .number()
    .transform((_, originalValue) => parseDecimalInput(originalValue))
    .typeError('Taxa BOB deve ser um número')
    .positive('Taxa BOB deve ser positiva')
    .required('Taxa BOB é obrigatória')
    .test('maxDecimals', 'Taxa BOB deve ter até 2 casas decimais', (_value, context) => {
      const original = context?.originalValue;
      const normalized = normalizeDecimalInput(original);
      if (!normalized) return true;
      return /^\d+([.,]\d{1,2})?$/.test(normalized);
    }),
}).required();

const Caixa = () => {
  const { register, watch, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      filtroData: new Date().toISOString().split('T')[0],
      USD: "0.00",
      BRL: "0.00",
      BOB: "0.00"
    },
  });

  const [isLoading, setIsLoading] = React.useState(false);
  const [taxasBloqueadas, setTaxasBloqueadas] = React.useState(false);
  const [taxas, setTaxas] = React.useState([]);
  const [filtroSaldoCaixa, setFiltroSaldoCaixa] = React.useState([]);
  const [financeiroDia, setFinanceiroDia] = React.useState([]);
  const [despesasDia, setDespesasDia] = React.useState([]);
  const [vendasDia, setVendasDia] = React.useState([]);
  const [lucrosDiarios, setLucrosDiarios] = React.useState({ TOTAL_BRL: 0, TOTAL_BOB: 0 });
  const [despesasDiarias, setDespesasDiarias] = React.useState([]);
  const [totais, setTotais] = React.useState({ USD: 0, BRL: 0, BOB: 0 });
  const [needsRates, setNeedsRates] = React.useState(false);
  const [tabelaTitulo, setTabelaTitulo] = React.useState('');
  const [tabelaLinhas, setTabelaLinhas] = React.useState([]);
  const [tabelaExpandido, setTabelaExpandido] = React.useState({});
  const carregarDetalhesFinanceiros = useCallback(async (dia) => {
    const paramsFin = { params: { dataInicio: '2000-01-01', dataFim: dia } };
    const paramsDiaOnly = { params: { dataInicio: dia, dataFim: dia } };
    try {
      const [fin, desp, ven] = await Promise.all([
        api.get('/financeiro', paramsFin),
        api.get('/despesas', paramsDiaOnly),
        api.get('/vendas', paramsDiaOnly),
      ]);
      return {
        financeiroDia: Array.isArray(fin.data) ? fin.data : [],
        despesasDia: Array.isArray(desp.data) ? desp.data : [],
        vendasDia: Array.isArray(ven.data) ? ven.data : [],
      };
    } catch (_) {
      return {
        financeiroDia: [],
        despesasDia: [],
        vendasDia: [],
      };
    }
  }, []);

  const carregarResumoOtimo = useCallback(async (dia) => {
    const r = await api.get('/caixa/resumo', { params: { data: dia } });
    const resumo = r?.data?.resumo || {};
    const taxObj = resumo.taxas || {};
    const taxasDia = resumo.hasTaxas && taxObj
      ? [{
          DATA: dia,
          USD: taxObj.USD || 1,
          BRL: taxObj.BRL || 0,
          BOB: taxObj.BOB || 0,
          USDT_BOB: resumo.USDT_BOB || taxObj.USDT_BOB || taxObj.USD || 0,
        }]
      : [];
    const saldosDia = Array.isArray(resumo?.bancos?.items) ? resumo.bancos.items : [];
    const detalhes = await carregarDetalhesFinanceiros(dia);
    return {
      resumo,
      taxasDia,
      saldosDia,
      ...detalhes,
      lucrosDiarios: { TOTAL_BRL: resumo.lucro?.BRL || 0, TOTAL_BOB: resumo.lucro?.BOB || 0 },
      despesasDiarias: [{ TOTAL: resumo.despesas?.total || 0, MOEDA: 'BOB', DATA: dia }],
    };
  }, [carregarDetalhesFinanceiros]);

  const buscarTaxaRegistrada = useCallback(async (dia) => {
    try {
      const respTx = await api.get('/consultas-saldos/taxas', { params: { data: dia } });
      const lista = Array.isArray(respTx.data) ? respTx.data : [];
      const reg = lista.find(t => String(t.DATA).slice(0,10) === dia) || lista[0];
      if (reg) {
        return { hasTax: true, taxas: [{ DATA: dia, USD: reg.USD, BRL: reg.BRL, BOB: reg.BOB }], registro: reg };
      }
      return { hasTax: false };
    } catch (_) {
      return { hasTax: false };
    }
  }, []);

  const carregarDadosFallback = useCallback(async (dia) => {
    const paramsFin = { params: { dataInicio: '2000-01-01', dataFim: dia } };
    const paramsDiaOnly = { params: { dataInicio: dia, dataFim: dia } };
    const [resTaxas, resSaldos, resFinan, resVendas, resDespesas] = await Promise.all([
      api.get("/consultas-saldos/taxas", paramsDiaOnly),
      api.get("/consultas-saldos/saldos", paramsDiaOnly),
      api.get("/financeiro", paramsFin),
      api.get(`/vendas/lucros-diarios/${dia}`),
      api.get(`/despesas/despesas-diarias/${dia}`),
    ]);
    let despFull = [];
    let venFull = [];
    try {
      const [despFullRes, venFullRes] = await Promise.all([
        api.get('/despesas', paramsDiaOnly),
        api.get('/vendas', paramsDiaOnly),
      ]);
      despFull = Array.isArray(despFullRes.data) ? despFullRes.data : [];
      venFull = Array.isArray(venFullRes.data) ? venFullRes.data : [];
    } catch (_) {
      despFull = [];
      venFull = [];
    }
    return {
      resTaxas,
      resSaldos,
      resFinan,
      resVendas,
      resDespesas,
      despFull,
      venFull,
    };
  }, []);

  const calcularSaldos = useCallback((taxasFiltradas, saldosFiltrados) => {
    if (!taxasFiltradas.length) return [];
    const taxa = taxasFiltradas[0];
    const saldosArray = Array.isArray(saldosFiltrados)
      ? saldosFiltrados
      : Object.values(saldosFiltrados).filter(item => item.ID !== undefined);
    return saldosArray.map(saldo => {
      const base = saldo.SOMA_SALDO !== undefined ? saldo.SOMA_SALDO : saldo.SALDO;
      const converted = convertCurrency(base, saldo.MOEDA, {
        USD: taxa.USD,
        BRL: taxa.BRL,
        BOB: taxa.BOB,
        USDT_BOB: taxa.USDT_BOB,
      });
      return {
        ...saldo,
        SALDO_USD: converted.usd.toFixed(2),
        SALDO_BOB: converted.bob.toFixed(2),
        SALDO_BRL: converted.brl.toFixed(2),
      };
    });
  }, []);

  const preencherComTaxasExternas = useCallback(async (diaISO) => {
    const dia = (String(diaISO || new Date().toISOString()).split('T')[0]) || new Date().toISOString().slice(0,10);
    try {
      const rates = await fetchRatesForDate(dia);
      if (!rates) {
        toast.warn('Não foi possível obter taxas externas válidas para esta data.');
        return null;
      }
      const usdValue = rates.USDT_BOB || rates.USD;
      if (!(usdValue > 0)) {
        toast.warn('Não foi possível obter USD válido para esta data.');
        return null;
      }
      const usdFormatted = usdValue;
      const brlValue = (rates.BRL && rates.BRL > 0) ? rates.BRL : undefined;
      const bobValue = (rates.BOB && rates.BOB > 0) ? rates.BOB : undefined;
      const converted = convertCurrency(usdValue, 'USD', {
        USD: usdValue,
        BRL: brlValue || rates.BRL,
        BOB: bobValue || rates.BOB,
        USDT_BOB: usdValue,
      });
      setValue('USD', usdFormatted.toFixed(2));
      setValue('BRL', (brlValue || converted.brl).toFixed(2));
      setValue('BOB', (bobValue || converted.bob).toFixed(2));
      setTaxas([{
        DATA: dia,
        USD: usdFormatted,
        BRL: brlValue || converted.brl,
        BOB: bobValue || converted.bob,
        USDT_BOB: usdValue,
        source: rates.source,
      }]);
      setTaxasBloqueadas(false);
      setNeedsRates(true);
      toast.info('Campos preenchidos com taxas externas; confirme antes de salvar.');
      return { USD: converted.usd, BRL: converted.brl, BOB: converted.bob, source: rates.source };
    } catch (error) {
      console.warn('Erro ao buscar taxas externas:', error);
      toast.warn('Erro ao buscar taxas externas. Preencha manualmente.');
      return null;
    }
  }, [fetchRatesForDate, setValue, setTaxas, setTaxasBloqueadas, setNeedsRates]);

  const aplicarDadosCaixa = useCallback(async (dia, payload) => {
    if (payload.origem === 'resumo') {
      const data = payload.valor;
      setTaxas(data.taxasDia);
      setFiltroSaldoCaixa(data.saldosDia);
      setFinanceiroDia(data.financeiroDia);
      setDespesasDia(data.despesasDia);
      setVendasDia(data.vendasDia);
      setLucrosDiarios(data.lucrosDiarios);
      setDespesasDiarias(data.despesasDiarias);
      const registro = await buscarTaxaRegistrada(dia);
      let effectiveTaxas = data.taxasDia;
      if (registro.hasTax) {
        const reg = registro.registro;
        setValue('USD', parseFloat(reg.USD || 1).toFixed(2));
        setValue('BRL', parseFloat(reg.BRL || 0).toFixed(2));
        setValue('BOB', parseFloat(reg.BOB || 0).toFixed(2));
        setTaxasBloqueadas(true);
        setNeedsRates(false);
      } else {
        setValue('USD', '');
        setValue('BRL', '');
        setValue('BOB', '');
        setTaxasBloqueadas(false);
        setNeedsRates(true);
        effectiveTaxas = [];
        const externalRates = await preencherComTaxasExternas(dia);
        if (externalRates) {
          effectiveTaxas = [{ DATA: dia, USD: externalRates.USD, BRL: externalRates.BRL, BOB: externalRates.BOB }];
        } else {
          effectiveTaxas = [];
        }
      }
      if (effectiveTaxas.length > 0) {
        const saldosCalculados = calcularSaldos(effectiveTaxas, data.saldosDia);
        const totalConv = saldosCalculados.reduce(
          (acc, item) => ({
            USD: acc.USD + parseFloat(item.SALDO_USD || 0),
            BRL: acc.BRL + parseFloat(item.SALDO_BRL || 0),
            BOB: acc.BOB + parseFloat(item.SALDO_BOB || 0),
          }),
          { USD: 0, BRL: 0, BOB: 0 }
        );
        setTotais(totalConv);
      } else {
        setTotais({ USD: 0, BRL: 0, BOB: 0 });
      }
      toast.success('Resumo carregado com sucesso!');
    } else if (payload.origem === 'fallback') {
      const fallback = payload.valor;
      setFinanceiroDia(Array.isArray(fallback.resFinan.data) ? fallback.resFinan.data : []);
      setDespesasDia(Array.isArray(fallback.despFull) ? fallback.despFull : []);
      setVendasDia(Array.isArray(fallback.venFull) ? fallback.venFull : []);
      setTaxas(fallback.resTaxas.data || []);
      setFiltroSaldoCaixa(fallback.resSaldos.data || []);
      setLucrosDiarios(fallback.resVendas.data || { TOTAL_BRL: 0, TOTAL_BOB: 0 });
      setDespesasDiarias(fallback.resDespesas.data || []);
      const dataFormatada = String(dia);
      const filteredTaxas = (Array.isArray(fallback.resTaxas.data) ? fallback.resTaxas.data : []).filter(taxa => taxa.DATA === dataFormatada);
      const filteredSaldos = (Array.isArray(fallback.resSaldos.data) ? fallback.resSaldos.data : []).filter(saldo => saldo.DATA_SALDO === dataFormatada);
      const filteredFinanceiro = (Array.isArray(fallback.resFinan.data) ? fallback.resFinan.data : []).filter(obj => obj.STATUS === "A PAGAR" && obj.TIPO === "Devo");
      const resContasReceber = (Array.isArray(fallback.resFinan.data) ? fallback.resFinan.data : []).filter(obj => obj.STATUS === "A PAGAR" && obj.TIPO === "Me deve");
      if (filteredTaxas.length === 0) {
        setValue('USD', '');
        setValue('BRL', '');
        setValue('BOB', '');
        setTaxasBloqueadas(false);
        setNeedsRates(true);
        setTotais({ USD: 0, BRL: 0, BOB: 0 });
        await preencherComTaxasExternas(dia);
        return;
      }
      const taxa = filteredTaxas[0];
      setValue('USD', parseFloat(taxa.USD).toFixed(2));
      setValue('BRL', parseFloat(taxa.BRL).toFixed(2));
      setValue('BOB', parseFloat(taxa.BOB).toFixed(2));
      setTaxasBloqueadas(true);
      setNeedsRates(false);
      setTaxas(filteredTaxas);
      const arrayCombinado = [
        ...filteredFinanceiro.map(item => ({
          ID: item.ID_CLIENTE,
          IDS: item.ID,
          ID_CREDOR: item.ID_CLIENTE,
          NOME_BANCO: item.NOME,
          SALDO: item.VALOR,
          MOEDA: item.MOEDA,
          TIPO: item.TIPO,
          STATUS: item.STATUS,
          DATA_SALDO: item.DATA_FORMATADA,
          LOGO_BANCO: item.LOGO_BANCO || "123.jpg",
          ORIGEM: 'PAGAR',
        })),
        ...filteredSaldos.map(item => ({
          ID: item.ID_BANCO,
          IDS: item.ID_SALDO,
          ID_BANCO: item.ID_BANCO,
          NOME_BANCO: item.NOME_BANCO,
          SALDO: item.SALDO,
          MOEDA: item.MOEDA,
          TIPO: item.TIPO || "Me deve",
          STATUS: item.STATUS || "A PAGAR",
          DATA_SALDO: item.DATA_SALDO,
          LOGO_BANCO: item.LOGO_BANCO,
          ORIGEM: 'BANCOS',
        })),
        ...resContasReceber.map(item => ({
          ID: item.ID_CLIENTE,
          IDS: item.ID,
          ID_DEVEDOR: item.ID_CLIENTE,
          NOME_BANCO: item.NOME,
          SALDO: item.VALOR,
          MOEDA: item.MOEDA,
          TIPO: item.TIPO,
          STATUS: item.STATUS,
          DATA_SALDO: item.DATA_FORMATADA,
          LOGO_BANCO: "123.jpg",
          ORIGEM: 'RECEBER',
        })),
      ];
      const resultado = calcularSaldos(filteredTaxas, Object.values(arrayCombinado));
      const total = resultado.reduce(
        (acc, item) => ({
          USD: acc.USD + parseFloat(item.SALDO_USD || 0),
          BRL: acc.BRL + parseFloat(item.SALDO_BRL || 0),
          BOB: acc.BOB + parseFloat(item.SALDO_BOB || 0),
        }),
        { USD: 0, BRL: 0, BOB: 0 }
      );
      setTotais(total);
      toast.success("Dados carregados com sucesso!");
    }
  }, [buscarTaxaRegistrada, calcularSaldos, setValue]);

  // Auto-preenche BOB quando usuário digitar USD/BRL (BOB = USD / BRL)
  const usdWatch = watch('USD');
  const brlWatch = watch('BRL');
  useEffect(() => {
    if (taxasBloqueadas) return; // quando taxa do dia está bloqueada, não recalcula
    const usd = parseFloat(String(usdWatch ?? '').toString().replace(',', '.'));
    const brl = parseFloat(String(brlWatch ?? '').toString().replace(',', '.'));
    if (Number.isFinite(usd) && Number.isFinite(brl) && brl > 0) {
      const bob = usd / brl;
      if (Number.isFinite(bob)) {
        setValue('BOB', bob.toFixed(2), { shouldValidate: true });
      }
    }
  }, [usdWatch, brlWatch, taxasBloqueadas, setValue]);

  // Observa os valores do formulário
  const filtroData = watch('filtroData');
  const [debouncedFiltroData] = useDebounce(filtroData, 300);

  // Função para buscar dados
  const fetchData = useCallback(async (data) => {
    setIsLoading(true);
    const dia = (String(data || '').split('T')[0]) || new Date().toISOString().slice(0,10);
    try {
      const resumoData = await carregarResumoOtimo(dia);
      await aplicarDadosCaixa(dia, { origem: 'resumo', valor: resumoData });
    } catch (_) {
      try {
        const fallbackData = await carregarDadosFallback(dia);
        await aplicarDadosCaixa(dia, { origem: 'fallback', valor: fallbackData });
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        toast.warn("Erro ao conectar com o banco de dados!");
      }
    } finally {
      setIsLoading(false);
    }
  }, [aplicarDadosCaixa, carregarDadosFallback, carregarResumoOtimo]);

  const liberarEdicaoTaxas = () => {
    setTaxasBloqueadas(false);
    setNeedsRates(true);
    toast.info('Taxas desbloqueadas. Atualize os valores e salve novamente.');
  };

  // useEffect para reagir a mudanças em debouncedFiltroData
  useEffect(() => {
    if (debouncedFiltroData) fetchData(debouncedFiltroData);
  }, [debouncedFiltroData, fetchData]);

  const toggleGrupo = (key) => {
    setTabelaExpandido(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const totaisTabela = useMemo(() => {
    return tabelaLinhas.reduce((acc, row) => {
      acc.usd += Number(row.usd || 0);
      acc.brl += Number(row.brl || 0);
      acc.bob += Number(row.bob || 0);
      if (row.children && tabelaExpandido[row.groupKey]) {
        row.children.forEach(child => {
          acc.usd += Number(child.usd || 0);
          acc.brl += Number(child.brl || 0);
          acc.bob += Number(child.bob || 0);
        });
      }
      return acc;
    }, { usd: 0, brl: 0, bob: 0 });
  }, [tabelaLinhas, tabelaExpandido]);

  // Função para salvar taxas
  const salvarTaxasEData = async (data) => {
    try {
      setIsLoading(true);
      const dataFormatada = data.filtroData.split('T')[0];
      const USDnum = parseDecimalInput(data.USD);
      const BRLnum = parseDecimalInput(data.BRL);
      const BOBnum = parseDecimalInput(data.BOB);
      if (!Number.isFinite(USDnum) || !Number.isFinite(BRLnum) || !Number.isFinite(BOBnum)) {
        toast.error('Valores inválidos. Verifique as taxas informadas.');
        return;
      }
      const novaTaxa = {
        DATA: dataFormatada,
        USD: USDnum.toFixed(2),
        BRL: BRLnum.toFixed(2),
        BOB: BOBnum.toFixed(2),
        USDT_BOB: USDnum.toFixed(2),
      };
      const resposta = await api.post("/consultas-saldos/taxas", novaTaxa);
      if (resposta.status === 201) {
        toast.success(`Taxas salvas com sucesso, DATA: ${resposta.data.data}`);
        setTaxas([{ ...novaTaxa }]);
        setTaxasBloqueadas(true);
        setNeedsRates(false);
        setValue('USD', parseFloat(novaTaxa.USD).toFixed(2));
        setValue('BRL', parseFloat(novaTaxa.BRL).toFixed(2));
        setValue('BOB', parseFloat(novaTaxa.BOB).toFixed(2));
        await fetchData(dataFormatada);
        setTaxasBloqueadas(true);
        setNeedsRates(false);
      }
    } catch (error) {
      console.error("Erro ao salvar taxas:", error);
      toast.error("Erro ao salvar taxas.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className={`${styles['caixa-root']} ${styles.root}`}>
      <h3 className={`${ui.titg}`}>Caixa Geral</h3>
      <form className={styles['caixa-filtros']} onSubmit={handleSubmit(salvarTaxasEData)}>
        <div className={styles['caixa-datafiltro']}>
          <input
            className={`${ui.inpdesktop}`}
            type="date"
            {...register('filtroData')}
          />
          <button
            
            type="button"
            className={styles.search}
            aria-label="Buscar dados do dia"
            title="Buscar dados do dia"
            onClick={() => fetchData(watch().filtroData)}
          >
            <LuSearch aria-hidden="true" focusable="false" />
          </button>
          {errors.filtroData && <span className={styles.error}>{errors.filtroData.message}</span>}
        </div>
        <div className={`${styles['caixa-conversao']} ${styles.usd}`}>
          <label className={`${ui.labdesktop}`} htmlFor="usdInput">USD</label>
          <input
            className={`${ui.inpdesktop}`}
            type="text"
            inputMode="decimal"
            id="usdInput"
            {...register('USD')}
            disabled={taxasBloqueadas}
            placeholder="USD"
          />
          {errors.USD && <span className={styles.error}>{errors.USD.message}</span>}
        </div>
        <div className={`${styles['caixa-conversao']} ${styles.brl}`}>
          <label className={`${ui.labdesktop}`} htmlFor="brlInput">BRL</label>
          <input
            className={`${ui.inpdesktop}`}
            type="text"
            inputMode="decimal"
            id="brlInput"
            {...register('BRL')}
            disabled={taxasBloqueadas}
            placeholder="BRL"
          />
          {errors.BRL && <span className={styles.error}>{errors.BRL.message}</span>}
        </div>
        <div className={`${styles['caixa-conversao']} ${styles.bob}`}>
          <label className={`${ui.labdesktop}`} htmlFor="bobInput">BOB</label>
          <input
            className={`${ui.inpdesktop}`}
            type="text"
            inputMode="decimal"
            id="bobInput"
            {...register('BOB')}
            disabled={taxasBloqueadas}
            placeholder="BOB"
          />
          {errors.BOB && <span className={styles.error}>{errors.BOB.message}</span>}
        </div>
        <div className={styles['caixa-botao']}>
          {taxasBloqueadas && (
            <button
              type="button"
              className={`${ui.butdesktop}`}
              aria-label="Editar taxas do dia"
              title="Editar taxas do dia"
              onClick={liberarEdicaoTaxas}
            >
              Editar taxas
            </button>
          )}
          {needsRates ? (
            <button
              type="submit"
              className={`${ui.butdesktop}`}
              aria-label="Salvar taxas do dia"
              title="Salvar taxas do dia"
            >
              Salvar Taxas
            </button>
          ) : (
            <button
              type="button"
              className={`${ui.butdesktop}`}
              aria-label="Atualizar dados do dia"
              title="Atualizar dados do dia"
              onClick={() => fetchData(watch().filtroData)}
            >
              Atualizar
            </button>
          )}
        </div>
      </form>

      {isLoading ? (
        <Spinner isLoading={isLoading} />
      ) : (
        <div className={styles['caixa-conteiner']}>
          <div className={styles['caixa-resumo']}>
            {(() => {
              const tx = (Array.isArray(taxas) && taxas[0]) || null;
              const fmt = (n, loc, cur) => Number(n || 0).toLocaleString(loc, { style: 'currency', currency: cur });
              // Lucro diário: base BRL/BOB vindos do backend; USD derivado
              const lucroBRL = lucrosDiarios?.TOTAL_BRL || 0;
              const lucroBOB = lucrosDiarios?.TOTAL_BOB || 0;
              const lucroUSD = tx ? (lucroBRL / (parseFloat(tx.BRL) || 1)) : 0;
              // A receber/pagar: somatório até a data do filtro (STATUS=A PAGAR)
              const toISO = (s)=> String(s||'').slice(0,10);
              const finList = Array.isArray(financeiroDia) ? financeiroDia : [];
              const finUpTo = finList.filter(f => toISO(f.DATA_FORMATADA || f.DATA) <= (filtroData || '') && String(f.STATUS).toUpperCase()==='A PAGAR');
              const sumToBob = (arr, tipo) => arr.filter(f => String(f.TIPO).toUpperCase()===tipo)
                .reduce((acc, f) => {
                  if (!tx) return acc;
                  const v = Math.abs(parseFloat(f.VALOR||0)||0);
                  const moeda = String(f.MOEDA||'BOB').toUpperCase();
                  // Converte para BOB: USD->BOB = USD*tx.USD; BRL->BOB = BRL*tx.BOB; BOB->BOB = v
                  let bob = 0;
                  if (moeda==='USD') bob = v*(parseFloat(tx.USD)||0);
                  else if (moeda==='BRL') bob = v*(parseFloat(tx.BOB)||0);
                  else bob = v;
                  return acc + bob;
                }, 0);
              const recBOB = sumToBob(finUpTo, 'ME DEVE');
              const pagBOB = sumToBob(finUpTo, 'DEVO');
              const recUSD = tx ? (recBOB / (parseFloat(tx.USD) || 1)) : 0;
              const recBRL = tx ? (recBOB / (parseFloat(tx.BOB) || 1)) : 0;
              const pagUSD = tx ? (pagBOB / (parseFloat(tx.USD) || 1)) : 0;
              const pagBRL = tx ? (pagBOB / (parseFloat(tx.BOB) || 1)) : 0;
              // Despesas: assumimos BOB no resumo
              const despBOB = (despesasDiarias?.[0]?.TOTAL) || 0;
              const despUSD = tx ? (despBOB / (parseFloat(tx.USD) || 1)) : 0;
              const despBRL = tx ? (despBOB / (parseFloat(tx.BOB) || 1)) : 0;

              const txa = tx ? { USD: parseFloat(tx.USD)||0, BRL: parseFloat(tx.BRL)||0, BOB: parseFloat(tx.BOB)||0, USDT_BOB: parseFloat(tx.USDT_BOB)||0 } : null;
              const toAmounts = (valor, moeda) => {
                if (!txa) return { usd: 0, brl: 0, bob: 0 };
                return convertCurrency(valor, moeda, txa);
              };

              const onShowBancos = () => {
                if (!txa) { toast.warn('Cadastre a taxa do dia para ver os detalhes.'); return; }
                const rows = (Array.isArray(filtroSaldoCaixa)?filtroSaldoCaixa:[]).map((s)=>{
                  const a = toAmounts(s.SALDO, s.MOEDA);
                  return {
                    data: s.DATA_SALDO,
                    nome: s.NOME_BANCO,
                    foto: s.LOGO_BANCO ? `${apiUrl}/logoBanco/${String(s.LOGO_BANCO).split('/').pop()}` : `${apiUrl}/logoBanco/123.jpg`,
                    usd: a.usd, brl: a.brl, bob: a.bob,
                    origem: 'BANCOS'
                  };
                });
                setTabelaTitulo('Bancos');
                setTabelaLinhas(rows);
              };

              const onShowReceber = () => {
                if (!txa) { toast.warn('Cadastre a taxa do dia para ver os detalhes.'); return; }
                const lista = (Array.isArray(finUpTo)?finUpTo:[]).filter(f => String(f.TIPO).toUpperCase()==='ME DEVE');
                const grupos = {};
                lista.forEach((f) => {
                  const nome = (f.NOME || f.DESCRICAO || '').trim();
                  const idc = f.ID_CLIENTE || '';
                  const key = `${idc}||${nome.toUpperCase()}`;
                  const a = toAmounts(f.VALOR, f.MOEDA);
                  if (!grupos[key]) {
                    grupos[key] = { key, idCliente: idc, nome, total: { usd:0, brl:0, bob:0 }, children: [] };
                  }
                  grupos[key].total.usd += a.usd;
                  grupos[key].total.brl += a.brl;
                  grupos[key].total.bob += a.bob;
                  grupos[key].children.push({ data: (String(f.DATA_FORMATADA||f.DATA).slice(0,10)), nome, foto:'', usd:a.usd, brl:a.brl, bob:a.bob, origem:'FINANCEIRO: RECEBER' });
                });
                const rows = Object.values(grupos).map(g => ({
                  data: (filtroData || ''),
                  nome: g.nome,
                  foto: '',
                  usd: g.total.usd,
                  brl: g.total.brl,
                  bob: g.total.bob,
                  origem: 'FINANCEIRO: RECEBER',
                  groupKey: g.key,
                  children: g.children,
                }));
                setTabelaTitulo('A Receber');
                setTabelaExpandido({});
                setTabelaLinhas(rows);
              };

              const onShowPagar = () => {
                if (!txa) { toast.warn('Cadastre a taxa do dia para ver os detalhes.'); return; }
                const lista = (Array.isArray(finUpTo)?finUpTo:[]).filter(f => String(f.TIPO).toUpperCase()==='DEVO');
                const grupos = {};
                lista.forEach((f) => {
                  const nome = (f.NOME || f.DESCRICAO || '').trim();
                  const idc = f.ID_CLIENTE || '';
                  const key = `${idc}||${nome.toUpperCase()}`;
                  const a = toAmounts(f.VALOR, f.MOEDA);
                  if (!grupos[key]) {
                    grupos[key] = { key, idCliente: idc, nome, total: { usd:0, brl:0, bob:0 }, children: [] };
                  }
                  grupos[key].total.usd += a.usd;
                  grupos[key].total.brl += a.brl;
                  grupos[key].total.bob += a.bob;
                  grupos[key].children.push({ data: (String(f.DATA_FORMATADA||f.DATA).slice(0,10)), nome, foto:'', usd:a.usd, brl:a.brl, bob:a.bob, origem:'FINANCEIRO: PAGAR' });
                });
                const rows = Object.values(grupos).map(g => ({
                  data: (filtroData || ''),
                  nome: g.nome,
                  foto: '',
                  usd: g.total.usd,
                  brl: g.total.brl,
                  bob: g.total.bob,
                  origem: 'FINANCEIRO: PAGAR',
                  groupKey: g.key,
                  children: g.children,
                }));
                setTabelaTitulo('A Pagar');
                setTabelaExpandido({});
                setTabelaLinhas(rows);
              };

              const onShowDespesas = () => {
                if (!txa) { toast.warn('Cadastre a taxa do dia para ver os detalhes.'); return; }
                const rows = (Array.isArray(despesasDia)?despesasDia:[]).map((d)=>{
                  const a = toAmounts(d.VALOR, d.MOEDA);
                  return { data: d.DATA_FORMATADA || d.DATA, nome: d.DESCRICAO || '', foto:'', usd:-a.usd, brl:-a.brl, bob:-a.bob, origem:'DESPESAS' };
                });
                setTabelaTitulo('Despesas do Dia');
                setTabelaLinhas(rows);
              };

              const onShowLucro = () => {
                if (!txa) { toast.warn('Cadastre a taxa do dia para ver os detalhes.'); return; }
                const rows = (Array.isArray(vendasDia)?vendasDia:[]).map((v)=>{
                  const brl = parseFloat(v.LUCROBRL||0)||0;
                  const bob = parseFloat(v.LUCROBOB||0)||0;
                  const usd = (txa.BRL>0? (brl/txa.BRL):0) + (txa.USD>0? (bob/txa.USD):0);
                  return { data: v.DATA, nome: v.NOME_CLIENTE || v.NOMEBRL || '', foto:'', usd, brl, bob, origem:'VENDAS/LUCRO' };
                });
                setTabelaTitulo('Lucro Diário');
                setTabelaLinhas(rows);
              };

              return (
                <>
                  <div className={`${styles['caixa-card']} ${styles['card-green']} ${styles['clickable']}`} onClick={onShowLucro}>
                    <h3 className={`${ui.titg}`}>Lucro</h3>
                    <p className='caixa-linha'>{fmt(lucroBOB, 'es-BO', 'BOB')}</p>
                    <p className='caixa-linha'>{fmt(lucroBRL, 'pt-BR', 'BRL')}</p>
                    <p className='caixa-linha'>{fmt(lucroUSD, 'en-US', 'USD')}</p>
                  </div>

                  <div className={`${styles['caixa-card']} ${styles['card-green']} ${styles['clickable']}`} onClick={onShowBancos}>
                    <h3 className={`${ui.titg}`}>Bancos</h3>
                    <p className='caixa-linha'>{fmt(totais.BOB, 'es-BO', 'BOB')}</p>
                    <p className='caixa-linha'>{fmt(totais.BRL, 'pt-BR', 'BRL')}</p>
                    <p className='caixa-linha'>{fmt(totais.USD, 'en-US', 'USD')}</p>
                  </div>

                  <div className={`${styles['caixa-card']} ${styles['card-yellow']} ${styles['clickable']}`} onClick={onShowReceber}>
                    <h3 className={`${ui.titg}`}>A Receber</h3>
                    <p className='caixa-linha'>{fmt(recBOB, 'es-BO', 'BOB')}</p>
                    <p className='caixa-linha'>{fmt(recBRL, 'pt-BR', 'BRL')}</p>
                    <p className='caixa-linha'>{fmt(recUSD, 'en-US', 'USD')}</p>
                  </div>

                  <div className={`${styles['caixa-card']} ${styles['card-red']} ${styles['clickable']}`} onClick={onShowPagar}>
                    <h3 className={`${ui.titg}`}>A Pagar</h3>
                    <p className='caixa-linha'>-{fmt(pagBOB, 'es-BO', 'BOB')}</p>
                    <p className='caixa-linha'>-{fmt(pagBRL, 'pt-BR', 'BRL')}</p>
                    <p className='caixa-linha'>-{fmt(pagUSD, 'en-US', 'USD')}</p>
                  </div>

                  <div className={`${styles['caixa-card']} ${styles['card-red']}`} onClick={onShowDespesas} style={{ cursor: 'pointer' }}>
                    <h3 className={`${ui.titg}`}>Despesas</h3>
                    <p className='caixa-linha'>-{fmt(despBOB, 'es-BO', 'BOB')}</p>
                    <p className='caixa-linha'>-{fmt(despBRL, 'pt-BR', 'BRL')}</p>
                    <p className='caixa-linha'>-{fmt(despUSD, 'en-US', 'USD')}</p>
                  </div>
                </>
              );
            })()}
          </div>
          {/* Tabela dinâmica de detalhes conforme card clicado */}
          <div className={styles['caixa-saldos']}>
            {tabelaTitulo ? (
              <h4 >{tabelaTitulo}</h4>
            ) : null}
            {Array.isArray(tabelaLinhas) && tabelaLinhas.length > 0 ? (
              <div className="tableScroll">
              <table className={styles['caixa-tabela-caixa']}>
                <thead>
                  <tr>
                    <th>DATA</th>
                    <th>FOTO/NOME</th>
                    <th>DÓLARES</th>
                    <th>REAIS</th>
                    <th>BOB</th>
                    <th>ORIGEM</th>
                  </tr>
                </thead>

                <tbody>
                  {tabelaLinhas.map((row, idx) => {
                    const parentKey = row.groupKey || row.ID || `row-${idx}`;
                    return (
                      <React.Fragment key={`row-${parentKey}`}>
                        <tr
                          onClick={row.children ? () => toggleGrupo(row.groupKey) : undefined}
                          style={{ cursor: row.children ? 'pointer' : 'default', fontWeight: row.children ? 'bold' : 'normal' }}
                        >
                          <td title={row.data}>{row.data}</td>
                          <td title={row.nome || '-'}>
                            <div className={styles['row-flex']}>
                              {row.foto ? <img src={row.foto} alt="foto" loading="lazy" className={styles['foto-mini']} /> : null}
                              <span>{row.nome || '-'}</span>
                              {row.children ? (
                                <span className={styles['row-extra']}>
                                  {tabelaExpandido[row.groupKey] ? '[-]' : '[+]'}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td title={Number(row.usd||0).toLocaleString('en-US', { style:'currency', currency:'USD' })}>{Number(row.usd||0).toLocaleString('en-US', { style:'currency', currency:'USD' })}</td>
                          <td title={Number(row.brl||0).toLocaleString('pt-BR', { style:'currency', currency:'BRL' })}>{Number(row.brl||0).toLocaleString('pt-BR', { style:'currency', currency:'BRL' })}</td>
                          <td title={Number(row.bob||0).toLocaleString('es-BO', { style:'currency', currency:'BOB' })}>{Number(row.bob||0).toLocaleString('es-BO', { style:'currency', currency:'BOB' })}</td>
                          <td title={row.origem}>{row.origem}</td>
                        </tr>

                        {row.children && tabelaExpandido[row.groupKey] ? row.children.map((child, cIdx) => {
                          const childKey = child.ID || child.id || `${parentKey}-child-${cIdx}`;
                          return (
                            <tr key={`det-${childKey}`}>
                              <td>{child.data}</td>
                              <td>
                              <div className={styles['row-flex']}>
                                {child.foto ? <img src={child.foto} alt="foto" loading="lazy" className={styles['foto-mini']} /> : null}
                                  <span>{child.nome || '-'}</span>
                                </div>
                              </td>
                              <td>{Number(child.usd||0).toLocaleString('en-US', { style:'currency', currency:'USD' })}</td>
                              <td>{Number(child.brl||0).toLocaleString('pt-BR', { style:'currency', currency:'BRL' })}</td>
                              <td>{Number(child.bob||0).toLocaleString('es-BO', { style:'currency', currency:'BOB' })}</td>
                              <td>{child.origem}</td>
                            </tr>
                          );
                        }) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>

                <tfoot>
                  <tr className={styles.totais}>
                    <td colSpan={2}>TOTAL</td>
                    <td>{totaisTabela.usd.toLocaleString('en-US', { style:'currency', currency:'USD' })}</td>
                    <td>{totaisTabela.brl.toLocaleString('pt-BR', { style:'currency', currency:'BRL' })}</td>
                    <td>{totaisTabela.bob.toLocaleString('es-BO', { style:'currency', currency:'BOB' })}</td>
                    <td>-</td>
                  </tr>
                </tfoot>
              </table>
              </div>
            ) : (
              <p style={{ marginTop: 8, textAlign: 'center' }}>Clique em um card acima para ver os detalhes.</p>
            )}
          </div>

          
          <ToastContainer limit={1} position="top-right" autoClose={3000} />
        </div>
      )}
    </main>
  );
};

export default Caixa;
